import React, { useEffect } from 'react';
import { ArrowLeft, Scale } from 'lucide-react';

interface AvisoLegalProps {
  navigateToPage: (page: string) => void;
}

export default function AvisoLegal({ navigateToPage }: AvisoLegalProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Cabecera / Banner superior */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white"></div>
          </div>
          
          <button
            onClick={() => navigateToPage('home')}
            className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors group focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 rounded-lg p-1"
            aria-label="Volver a la página de inicio"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Volver al inicio
          </button>
          
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
              <Scale className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Aviso Legal y Condiciones</h1>
              <p className="text-blue-100 text-sm mt-1">Términos de servicio y propiedad del sitio web</p>
            </div>
          </div>
        </div>

        {/* Contenido Legal */}
        <div className="p-8 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Datos Identificativos del Titular de la Web</h2>
            <p className="mb-2">
              En cumplimiento del deber de información recogido en el artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE), se facilitan a continuación los siguientes datos del propietario de este sitio web:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <li><strong>Titular de la actividad:</strong> José Luis Construcciones</li>
              <li><strong>CIF / NIF:</strong> B-XXXXXXXX (Pendiente de sustituir)</li>
              <li><strong>Domicilio Social:</strong> C. Ing. García Pimentel, 37, 23600 Martos, Jaén</li>
              <li><strong>Teléfono de contacto:</strong> 953 03 95 67 / 650 44 13 96</li>
              <li><strong>Correo electrónico:</strong> construccionesperezrosa@gmail.com</li>
              <li><strong>Sitio Web:</strong> joseluisconstrucciones.com</li>
            </ul>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Condiciones Generales de Uso</h2>
            <p>
              El acceso y uso de este sitio web le atribuye la condición de <strong>USUARIO</strong>. Desde el momento de dicho acceso, usted acepta expresamente y sin reservas las Condiciones Generales de Uso aquí reflejadas.
            </p>
            <p className="mt-2">
              Este sitio web proporciona información sobre los servicios de reformas, construcción, saneamiento, electricidad y catálogo estático de productos (grifería, platos de ducha, sanitarios, etc.) de José Luis Construcciones. El usuario se compromete a realizar un uso adecuado de los contenidos y servicios de conformidad con la ley, la moral, las buenas costumbres y el orden público, absteniéndose de realizar cualquier acto ilícito o que pueda dañar la integridad y el funcionamiento del sitio web.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Carácter del Catálogo de Productos (No Venta Online)</h2>
            <p className="text-blue-900 font-medium bg-blue-50/50 p-4 rounded-xl border border-blue-100">
              ⚠️ IMPORTANTE: El apartado de "Productos" de este sitio web tiene carácter de catálogo estático e informativo de exposición. No se realiza comercio electrónico, venta online directa ni pasarela de pagos. Los precios y características son informativos y válidos para compra física o contratación en nuestras instalaciones, sujetos a disponibilidad de stock y presupuestos formales.
            </p>
            <p className="mt-2">
              Para adquirir cualquiera de los productos listados o solicitar productos de catálogos específicos, el usuario deberá contactar con José Luis Construcciones a través de los canales provistos en la sección de contacto.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Propiedad Intelectual e Industrial</h2>
            <p>
              José Luis Construcciones es titular o licenciatario de todos los derechos de propiedad intelectual e industrial de este sitio web, así como de los elementos contenidos en el mismo (a título enunciativo: logotipos, imágenes, textos, marcas, código fuente, estructura de navegación, combinación de colores, diseño gráfico, etc.).
            </p>
            <p className="mt-2">
              Queda expresamente prohibida la reproducción, distribución, comunicación pública y transformación de la totalidad o parte de los contenidos de esta página web con fines comerciales, en cualquier soporte y por cualquier medio técnico, sin la autorización previa y por escrito de José Luis Construcciones. El usuario podrá visualizar e incluso imprimir fragmentos exclusivamente para su uso personal y privado.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Limitación de Responsabilidad</h2>
            <p>
              José Luis Construcciones no se hace responsable, en ningún caso, de los daños y perjuicios de cualquier naturaleza que pudieran ocasionar, a título enunciativo: errores u omisiones en los contenidos informativos, falta de disponibilidad del portal o la transmisión de virus o programas maliciosos en los contenidos, a pesar de haber adoptado todas las medidas tecnológicas necesarias para evitarlo.
            </p>
            <p className="mt-2">
              La información contenida en esta web sobre presupuestos estimativos o plazos es meramente indicativa y deberá concretarse mediante un contrato formal de obra y servicios firmado por ambas partes.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Enlaces de Terceros</h2>
            <p>
              En el caso de que en la web se dispongan enlaces o hipervínculos hacia otros sitios de Internet (como perfiles de redes sociales o proveedores de mapas), José Luis Construcciones no ejercerá ningún tipo de control sobre dichos sitios y contenidos, ni asumirá responsabilidad alguna por los daños derivados del acceso a los mismos.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Protección de Datos Personales</h2>
            <p>
              Cualquier tratamiento de datos personales que se lleve a cabo a través de este sitio web se regirá en su totalidad por lo dispuesto en nuestra{' '}
              <button
                onClick={() => navigateToPage('privacidad')}
                className="text-blue-600 hover:text-blue-800 font-semibold underline focus:outline-none"
              >
                Política de Privacidad
              </button>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Modificaciones, Legislación Aplicable y Jurisdicción</h2>
            <p>
              José Luis Construcciones se reserva el derecho de efectuar sin previo aviso las modificaciones que considere oportunas en su portal, pudiendo cambiar, suprimir o añadir tanto los contenidos y servicios que se presten a través de la misma como la forma en la que estos aparezcan presentados o localizados.
            </p>
            <p className="mt-2">
              La relación entre José Luis Construcciones y el usuario se regirá por la normativa española vigente. Cualquier controversia se someterá a los Juzgados y Tribunales de la ciudad de <strong>Jaén, España</strong>, renunciando expresamente las partes a cualquier otro fuero que pudiera corresponderles.
            </p>
          </section>
        </div>

        {/* Footer interior de la página */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 flex justify-between items-center flex-wrap gap-4 text-sm text-gray-500">
          <span>Última actualización: Julio de 2026</span>
          <button
            onClick={() => navigateToPage('home')}
            className="text-blue-600 hover:text-blue-800 font-semibold underline focus:outline-none"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    </div>
  );
}
